import { bookInTx, moveInTx, type BookingDraft, type BookResult, type MoveRequest, type MoveResult } from '../../domain/booking';
import type { ListFilter, Tx } from '../../domain/tx';
import { ENTITY_NAMES, type EntityMap, type EntityName, type Id, type IsoUtc } from '../../domain/types';
import { BOOKING_STORES, type ChangeEvent, type Dump, type Repo, type SeedData, type StorageAdapter, type Tables, type Unsubscribe } from '../types';

export interface Change { entity: EntityName; type: 'put' | 'delete'; id: Id }
export interface Executed<R> { value: R; changes: Change[] }

export function emptyTables(): Tables {
  return Object.fromEntries(ENTITY_NAMES.map((n) => [n, []])) as unknown as Tables;
}

/** Shared behaviour: subscriptions, cross-tab broadcast, repos, booking, import/export. */
export abstract class BaseStorage implements StorageAdapter {
  abstract readonly kind: 'indexeddb' | 'memory' | 'supabase';
  private readonly listeners = new Map<EntityName | '*', Set<(e: ChangeEvent) => void>>();
  private channel: BroadcastChannel | undefined;

  protected constructor(channelName?: string) {
    if (channelName && typeof BroadcastChannel !== 'undefined') {
      this.channel = new BroadcastChannel(channelName);
      this.channel.onmessage = (m: MessageEvent<ChangeEvent[]>) => this.emit(m.data.map((e) => ({ ...e, remote: true })), false);
    }
  }

  protected abstract execute<R>(entities: readonly EntityName[], mode: 'r' | 'rw', fn: (tx: Tx) => Promise<R>): Promise<Executed<R>>;
  protected abstract replaceAll(tables: Tables): Promise<void>;
  protected abstract dumpAll(): Promise<Tables>;
  protected abstract closeImpl(): void;

  protected emit(events: ChangeEvent[], broadcast: boolean): void {
    for (const e of events) {
      for (const cb of [...(this.listeners.get(e.entity) ?? [])]) cb(e);
      for (const cb of [...(this.listeners.get('*') ?? [])]) cb(e);
    }
    if (broadcast && this.channel && events.length > 0) this.channel.postMessage(events);
  }

  subscribe(entity: EntityName | '*', cb: (e: ChangeEvent) => void): Unsubscribe {
    const set = this.listeners.get(entity) ?? new Set();
    set.add(cb);
    this.listeners.set(entity, set);
    return () => void set.delete(cb);
  }

  async transact<R>(entities: readonly EntityName[], fn: (tx: Tx) => Promise<R>): Promise<R> {
    const { value, changes } = await this.execute(entities, 'rw', fn);
    this.emit(changes.map((c) => ({ ...c, remote: false })), true);
    return value;
  }

  repo<E extends EntityName>(entity: E): Repo<E> {
    type Row = EntityMap[E];
    return {
      get: async (id: Id) => (await this.execute([entity], 'r', (tx) => tx.get(entity, id))).value as Row | undefined,
      list: async (filter?: ListFilter<Row>) => (await this.execute([entity], 'r', (tx) => tx.list(entity, filter))).value as Row[],
      put: async (row: Row) => {
        await this.transact([entity], (tx) => tx.put(entity, row));
        return row;
      },
      delete: (id: Id) => this.transact([entity], (tx) => tx.delete(entity, id)),
      subscribe: (cb) => this.subscribe(entity, cb),
    };
  }

  bookAppointment(draft: BookingDraft, now: IsoUtc): Promise<BookResult> {
    return this.transact(BOOKING_STORES, (tx) => bookInTx(tx, draft, now));
  }
  moveAppointment(req: MoveRequest, now: IsoUtc): Promise<MoveResult> {
    return this.transact(BOOKING_STORES, (tx) => moveInTx(tx, req, now));
  }

  async exportAll(): Promise<Dump> {
    return { version: 1, exportedAt: new Date().toISOString(), data: await this.dumpAll() };
  }
  async importAll(dump: Dump): Promise<void> {
    if (dump.version !== 1) throw new Error(`unsupported dump version ${String(dump.version)}`);
    await this.load({ ...emptyTables(), ...dump.data });
  }
  async reset(seed?: SeedData): Promise<void> {
    await this.load({ ...emptyTables(), ...seed });
  }
  private async load(tables: Tables): Promise<void> {
    await this.replaceAll(tables);
    this.emit(ENTITY_NAMES.map((entity) => ({ entity, type: 'reset' as const, remote: false })), true);
  }
  close(): void {
    this.closeImpl();
    this.channel?.close();
  }
}
